import {createSelector} from 'reselect'

import {
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  onlyIfForm,
  getState,
  useSelector,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import Checkbox from '../../../common/components/Checkbox.js'
import Radio from '../../../common/components/Radio.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import {saveUserSettings, userSettingSelector} from '../../../data/me.js'
import {isNonZeroPositiveNumeric} from '../../../common/utils.js'
import Icon from '../../../common/icons/Icon.js'

const MODAL_FORM = 'stocktake_settings_modal'

const STOCKTAKE__NAVIGATE_ON_COUNT_SAVE = 'stocktake.navigate_on_count_save'
const STOCKTAKE__AUTOSAVE_COUNTS = 'stocktake.autosave_counts'
export const STOCKTAKE__MULTIPLIER = 'stocktake.multiplier'
const STOCKTAKE__SHOW_MULTIPLIER_ON_COUNT_SCREEN =
  'stocktake.show_multiplier_on_count_screen'
export const STOCKTAKE__AUTOCOUNT_ON_SCAN_FOR_KITS =
  'stocktake.autocount_on_scan_for_kits'
export const STOCKTAKE__ON_NEW_PRODUCT_SCAN = 'stocktake.on_new_product_scan'
export const STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT =
  'add_do_not_count'
export const STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT = 'add_and_count'
export const STOCKTAKE__ON_NEW_PRODUCT_SCAN__WARNING = 'warning'

export function stocktakeNavigateOnCountSaveSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__NAVIGATE_ON_COUNT_SAVE,
    defaultValue: true,
  })
}

export function stocktakeAutosaveCountsSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__AUTOSAVE_COUNTS,
    defaultValue: false,
  })
}

export function stocktakeMultiplierSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__MULTIPLIER,
    defaultValue: 1,
  })
}

export function stocktakeShowMultiplierOnCountScreenSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__SHOW_MULTIPLIER_ON_COUNT_SCREEN,
    defaultValue: false,
  })
}

export function stocktakeAutocountOnScanForKitsSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__AUTOCOUNT_ON_SCAN_FOR_KITS,
    defaultValue: false,
  })
}

export function stocktakeOnNewProductScanSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__ON_NEW_PRODUCT_SCAN,
    defaultValue: STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT,
  })
}

export function showStocktakeSettingsModal() {
  setForm(MODAL_FORM, {
    navigate_on_count_save: stocktakeNavigateOnCountSaveSelector(getState()),
    autosave_counts: stocktakeAutosaveCountsSelector(getState()),
    multiplier: stocktakeMultiplierSelector(getState()),
    show_multiplier_on_count_screen:
      stocktakeShowMultiplierOnCountScreenSelector(getState()),
    autocount_on_scan_for_kits:
      stocktakeAutocountOnScanForKitsSelector(getState()),
    on_new_product_scan: stocktakeOnNewProductScanSelector(getState()),
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export function errorsSelector(state) {
  const {multiplier} = modalFormSelector(state)
  const errors = {}

  if (!isNonZeroPositiveNumeric(multiplier)) {
    errors.multiplier = 'Multiplier must be a positive non-zero number'
    errors.preventSave = true
  }

  return errors
}

export async function saveStocktakeSettings() {
  try {
    updateModalForm({isSaving: true})

    const {
      navigate_on_count_save,
      autosave_counts,
      multiplier,
      show_multiplier_on_count_screen,
      autocount_on_scan_for_kits,
      on_new_product_scan,
    } = modalFormSelector(getState())

    await saveUserSettings({
      [STOCKTAKE__NAVIGATE_ON_COUNT_SAVE]: navigate_on_count_save,
      [STOCKTAKE__AUTOSAVE_COUNTS]: autosave_counts,
      [STOCKTAKE__MULTIPLIER]: Number(multiplier),
      [STOCKTAKE__SHOW_MULTIPLIER_ON_COUNT_SCREEN]:
        show_multiplier_on_count_screen,
      [STOCKTAKE__AUTOCOUNT_ON_SCAN_FOR_KITS]: autocount_on_scan_for_kits,
      [STOCKTAKE__ON_NEW_PRODUCT_SCAN]: on_new_product_scan,
    })

    showMessageToast('Settings Saved')

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: `Error saving stocktake settings: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function StocktakeSettingsModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      className="modal--fit-screen-sm"
      title="Stocktake Settings"
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={() => saveStocktakeSettings()}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      error={form.serverError}
      isDisabled={errors.preventSave}
    >
      <>
        <dl className="list margin-bottom-40">
          <dt className="list__title fs-01 lh-md margin-bottom-15">General</dt>
          <dd className="list__item margin-bottom-15">
            <Checkbox
              id="navigate_on_count_save"
              label="Proceed to next SKU on manual count save"
              checked={form.navigate_on_count_save}
              onChange={(navigate_on_count_save) =>
                updateModalForm({navigate_on_count_save})
              }
            />
          </dd>
          <dd className="list__item margin-bottom-15">
            <Checkbox
              id="autosave_counts"
              label="Autosave counts"
              checked={form.autosave_counts}
              onChange={(autosave_counts) => updateModalForm({autosave_counts})}
            />
            <div className="fs-n2 lh-md op-75 margin-top-5">
              <em>Autosave occurs 10 seconds after a change is detected.</em>
            </div>
          </dd>
        </dl>
        <dl className="list margin-bottom-0">
          <dt className="list__title fs-01 lh-md margin-bottom-15">
            Barcode Scanning
          </dt>
          <dd className="list__item margin-bottom-30">
            <div className="margin-bottom-15">
              <label
                htmlFor="multiplier"
                className="fs-00 lh-md margin-bottom-15"
              >
                Set value for scan multiplier:
              </label>
              <div className="flex align-items-center">
                <div className="flex margin-right-15 padding-right-15 divider--right">
                  <div className="margin-bottom-0">
                    <Icon
                      name="scanner"
                      className="text--md-grey fs-02 lh-sm v-align-middle margin-right-7"
                    />
                    <span className="text--lt-grey fs-01 lh-sm v-align-middle margin-right-10">
                      x
                    </span>
                  </div>
                  <div className="margin-bottom-0">
                    <NumberInput
                      id="multiplier"
                      value={form.multiplier}
                      onChange={(multiplier) => updateModalForm({multiplier})}
                      errorMessage={errors.multiplier}
                    />
                  </div>
                </div>
                <div>
                  <Checkbox
                    id="show_multiplier_on_count_screen"
                    label="Show multiplier input on count screen"
                    checked={form.show_multiplier_on_count_screen}
                    onChange={(show_multiplier_on_count_screen) =>
                      updateModalForm({show_multiplier_on_count_screen})
                    }
                  />
                </div>
              </div>
            </div>
          </dd>
          <dd className="list__item margin-bottom-30">
            <Checkbox
              id="autocount_on_scan_for_kits"
              label="Autocount on Scan for Kits"
              checked={form.autocount_on_scan_for_kits}
              onChange={(autocount_on_scan_for_kits) =>
                updateModalForm({autocount_on_scan_for_kits})
              }
            />
          </dd>
          <dd className="list__item margin-bottom-0">
            <div className="margin-bottom-15">
              <label htmlFor="on_new_product_scan" className="fs-00 lh-md">
                Behavior for when scanning a product barcode that is not on
                stocktake:
              </label>
            </div>
            <div className="margin-bottom-15">
              <Radio
                mode="fancy"
                id="on_new_product_scan__add_do_not_count"
                label="Automatically add product, but do not increase count"
                checked={
                  form.on_new_product_scan ===
                  STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT
                }
                onChange={() =>
                  updateModalForm({
                    on_new_product_scan:
                      STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT,
                  })
                }
              />
            </div>
            <div className="margin-bottom-15">
              <Radio
                mode="fancy"
                id="on_new_product_scan__add_and_count"
                label="Automatically add product and increase count"
                checked={
                  form.on_new_product_scan ===
                  STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT
                }
                onChange={() =>
                  updateModalForm({
                    on_new_product_scan:
                      STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT,
                  })
                }
              />
            </div>
            <div>
              <Radio
                mode="fancy"
                id="on_new_product_scan__warning"
                label="Display a warning"
                checked={
                  form.on_new_product_scan ===
                  STOCKTAKE__ON_NEW_PRODUCT_SCAN__WARNING
                }
                onChange={() =>
                  updateModalForm({
                    on_new_product_scan:
                      STOCKTAKE__ON_NEW_PRODUCT_SCAN__WARNING,
                  })
                }
              />
            </div>
          </dd>
        </dl>
      </>
    </ConfirmModal>
  )
}

export default onlyIfForm(StocktakeSettingsModal, modalFormSelector)
